import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeftRight,
  Bus,
  Coffee,
  Compass,
  HandCoins,
  Hotel,
  MoreHorizontal,
  Percent,
  Plane,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Ticket,
  Utensils,
  Wallet,
} from "lucide-react";

import {
  computeDirectDebts,
  formatMoney,
  type ExpenseEntry,
  type ExpenseType,
  type Trip,
  type TripBudget,
} from "@travelix/core";

import { useTrip } from "@/hooks/use-trip";
import { useCountUp } from "@/hooks/use-count-up";
import { EmptyState } from "@/components/empty-state";
import { MoneyText } from "@/components/money-text";
import { DebtRow } from "@/features/expenses/settle-up";
import { Skeleton } from "@/components/ui/skeleton";
import { isExpenseLoggingClosed } from "@/lib/trip-readonly";
import { celebrate } from "@/lib/celebrate";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-provider";

const CATEGORIES: { key: ExpenseType; label: string; color: string }[] = [
  { key: "restaurants", label: "Restaurants", color: "#E4572E" },
  { key: "drinks",      label: "Drinks",      color: "#B87E28" },
  { key: "hotels",      label: "Hotels",      color: "#C97B3F" },
  { key: "flights",     label: "Flights",     color: "#4A7C9E" },
  { key: "transportation", label: "Transport", color: "#5E8A6E" },
  { key: "activities",  label: "Activities",  color: "#D08A3F" },
  { key: "tickets",     label: "Tickets",     color: "#D9A85A" },
  { key: "shopping",    label: "Shopping",    color: "#A16CB0" },
  { key: "groceries",   label: "Groceries",   color: "#5E8A6E" },
  { key: "misc",        label: "Misc",        color: "#8B837C" },
];

export default function ExpensesTab() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: trip, isLoading } = useTrip(id);
  // Timeline first — the chronological view is the easiest way to
  // track what's been spent/settled as the trip goes, so it's both
  // the leftmost tab and the default.
  const [tab, setTab] = useState<"timeline" | "category" | "friend">("timeline");
  // Expenses keep a week-long grace period past the trip's end date
  // before edit/delete lock — see isExpenseLoggingClosed.
  const readOnly = isExpenseLoggingClosed(trip);
  // Editing reuses the Add Expense screen with ?editId=X, so every
  // field (payer, split mode, per-member weights, currency, date) is
  // available in the same UI as adding — no separate limited sheet.
  // Payments are a distinct, much smaller form (record-payment), so
  // they route there instead.
  const openEdit = (entry: ExpenseEntry) =>
    navigate(
      entry.type === "payment"
        ? `/trip/${id}/record-payment?editId=${entry.id}`
        : `/trip/${id}/add-expense?editId=${entry.id}`,
    );

  // allExpenses feeds balance calc (payments must count) and the
  // Timeline tab; spendExpenses excludes settlements so they never
  // inflate "money spent" (trip total, per-person, By category). Trip
  // lists are small enough that a plain filter per render is cheap —
  // no need to memoize against the ?? [] fallback's unstable identity.
  const allExpenses = trip?.expenses ?? [];
  const spendExpenses = allExpenses.filter((e) => e.type !== "payment");
  const currency = trip?.currency ?? "EUR";
  const totalRaw = spendExpenses.reduce((s, e) => s + e.amount, 0);
  const memberCount = trip?.memberIds.length ?? 0;
  const perPersonRaw = memberCount > 0 ? totalRaw / memberCount : 0;
  // Tween the summary numbers so a new expense counts up instead of
  // hard-swapping. See useCountUp for the reduced-motion path. Called
  // before the loading/not-ready returns so the hook order stays
  // stable (React's rules-of-hooks).
  const total = useCountUp(totalRaw);
  const perPerson = useCountUp(perPersonRaw);

  if (isLoading) return <LoadingBlock />;
  if (!trip) return null;

  return (
    <div className="mx-auto max-w-2xl px-5 pt-4">
      {/* Summary card */}
      <section
        data-tour="expenses-summary"
        className="rounded-xl border border-line bg-bg-elevated p-5 shadow-warm-1"
        aria-labelledby="expenses-summary-heading"
      >
        <p
          id="expenses-summary-heading"
          className="text-[11px] font-bold uppercase tracking-wider text-ink-subtle"
        >
          Trip total
        </p>
        <MoneyText
          as="p"
          amount={total}
          currency={currency}
          className="mt-1 text-3xl font-bold tracking-tight text-ink"
          aria-live="polite"
        />
        {trip.budget ? <BudgetProgress spent={total} budget={trip.budget} /> : null}
        <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
          <span className="text-[13px] text-ink-muted">
            Per person · {trip.memberIds.length} traveler{trip.memberIds.length === 1 ? "" : "s"}
          </span>
          <MoneyText amount={perPerson} currency={currency} className="text-[15px] font-semibold text-ink" />
        </div>
      </section>

      {/* Tab switcher */}
      <div role="tablist" data-tour="expenses-tabs" className="mt-5 flex gap-1 rounded-full bg-bg-sunken p-1">
        {(["timeline", "category", "friend"] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              "flex-1 rounded-full py-2 text-[13px] font-semibold transition-colors",
              tab === t ? "bg-bg-elevated text-ink shadow-warm-1" : "text-ink-subtle hover:text-ink",
            )}
          >
            {t === "timeline" ? "Timeline" : t === "category" ? "By category" : "Who owes who"}
          </button>
        ))}
      </div>

      {allExpenses.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No expenses yet"
          body="Tap + to log the first one. Balances update automatically."
        />
      ) : tab === "timeline" ? (
        <TimelineExpenseList
          trip={trip}
          expenses={allExpenses}
          currency={currency}
          onEdit={readOnly ? undefined : openEdit}
        />
      ) : tab === "category" ? (
        <CategoryList
          expenses={spendExpenses}
          currency={currency}
          onEdit={readOnly ? undefined : openEdit}
        />
      ) : (
        <FriendList
          trip={trip}
          expenses={allExpenses}
          currency={currency}
          onEdit={readOnly ? undefined : openEdit}
        />
      )}
    </div>
  );
}

// ─── Budget progress ─────────────────────────────────────────
/**
 * Slim progress bar under the trip total. Fills coral up to 60 %,
 * amber up to 90 %, danger past that. Assumes budget currency matches
 * the trip currency — mismatches are labelled but not FX-converted
 * (a budget in a different currency is an unusual case we don't
 * spend runtime FX on for a display line).
 */
function BudgetProgress({ spent, budget }: { spent: number; budget: TripBudget }) {
  const pct = budget.amount > 0 ? Math.min(1.5, spent / budget.amount) : 0;
  const clamped = Math.min(1, pct);
  const bar =
    pct >= 0.9 ? "bg-danger" : pct >= 0.6 ? "bg-warning" : "bg-primary";
  const label =
    pct >= 1
      ? "Over budget"
      : `${Math.round(pct * 100)}% of ${formatMoney(budget.amount, budget.currency)}`;
  return (
    <div className="mt-3">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-sunken" aria-hidden>
        <div
          className={cn("h-full rounded-full transition-all", bar)}
          style={{ width: `${clamped * 100}%` }}
        />
      </div>
      <p className="mt-1 text-[12px] text-ink-subtle m-0" aria-live="polite">
        {label}
      </p>
    </div>
  );
}

// ─── By category ─────────────────────────────────────────────
/**
 * Groups the trip's expenses by ExpenseType, each group with its own
 * header (icon + category name + running total). Groups sort by
 * descending category total so the biggest spends land at the top.
 *
 * The tab used to render a flat, insertion-ordered list under a "By
 * category" label — misleading. This version actually delivers on the
 * label; the raw chronological list moved to the sibling variant in
 * Phase 4.6.
 */
function CategoryList({
  expenses,
  currency,
  onEdit,
}: {
  expenses: ExpenseEntry[];
  currency: string;
  /** Undefined means read-only — rows render as non-interactive divs. */
  onEdit?: (e: ExpenseEntry) => void;
}) {
  const currentUid = useAuth().user?.uid ?? null;
  const readOnly = !onEdit;

  const groups = useMemo(() => {
    const byType = new Map<ExpenseType, ExpenseEntry[]>();
    for (const e of expenses) {
      const arr = byType.get(e.type) ?? [];
      arr.push(e);
      byType.set(e.type, arr);
    }
    // Sort inside each group so the most recent expense is on top.
    for (const arr of byType.values()) {
      arr.sort((a, b) => b.expenseDate.toMillis() - a.expenseDate.toMillis());
    }
    // Group order: descending total.
    return [...byType.entries()]
      .map(([type, items]) => ({
        type,
        items,
        total: items.reduce((s, e) => s + e.amount, 0),
      }))
      .sort((a, b) => b.total - a.total);
  }, [expenses]);

  return (
    <div className="mt-5 flex flex-col gap-5">
      {groups.map((g) => {
        const Icon = iconForCategory(g.type);
        const color = colorForCategory(g.type);
        const label = CATEGORIES.find((c) => c.key === g.type)?.label ?? cap(g.type);
        return (
          <section key={g.type} aria-label={label}>
            <header className="mb-2 flex items-center justify-between px-1">
              <span className="inline-flex items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-md"
                  style={{ background: color + "22", color }}
                  aria-hidden
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                </span>
                <span className="text-[13px] font-semibold text-ink">
                  {label}
                </span>
                <span className="text-[12px] text-ink-subtle">
                  · {g.items.length}
                </span>
              </span>
              <MoneyText
                amount={g.total}
                currency={currency}
                className="text-[13px] font-semibold tabular text-ink-muted"
              />
            </header>
            <ul className="flex flex-col gap-2">
              {g.items.map((e) => {
                const splitInfo =
                  e.splits.length > 1
                    ? `Split ${e.splits.length} ways`
                    : currentUid === (e.payerUid ?? e.creatorId)
                      ? "Paid by you"
                      : `Paid by ${e.payerName ?? e.creatorName}`;
                const rowInner = (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-semibold text-ink m-0 truncate">{e.name}</p>
                      <p className="text-[12px] text-ink-subtle m-0">{splitInfo}</p>
                    </div>
                    <MoneyText amount={e.amount} currency={currency} className="text-[15px] font-semibold text-ink" />
                  </>
                );
                return (
                  <li key={e.id} className="rounded-lg border border-line bg-bg-elevated shadow-warm-1">
                    {readOnly ? (
                      <div className="flex w-full items-center gap-3 p-3">
                        {rowInner}
                      </div>
                    ) : (
                      // Tap opens the edit screen; delete lives there behind a
                      // confirmation. No row swipe — horizontal gestures collide
                      // with iOS edge-swipe navigation and caused accidental edits/deletes.
                      <button
                        type="button"
                        onClick={() => onEdit!(e)}
                        aria-label={`Edit ${e.name}, ${formatMoney(e.amount, currency)}`}
                        className="flex w-full items-center gap-3 rounded-lg p-3 text-left"
                      >
                        {rowInner}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// ─── Timeline (chronological) ────────────────────────────────
/**
 * Every expense — spending and settlements alike — grouped by the
 * local day it happened, in the order it occurred, so people can see
 * the sequence money moved during the trip. Mirrors the day-by-day
 * pattern in trip.$id.timeline.tsx (groupByDay / localYmd / sticky
 * jump strip), adapted for expenseDate instead of activity startTime.
 * Unlike that timeline, days with no expenses are skipped entirely —
 * there's no "plan this day" prompt here, just a browsable record.
 */
function TimelineExpenseList({
  trip,
  expenses,
  currency,
  onEdit,
}: {
  trip: Trip;
  expenses: ExpenseEntry[];
  currency: string;
  /** Undefined means read-only — rows render as non-interactive divs. */
  onEdit?: (e: ExpenseEntry) => void;
}) {
  const currentUid = useAuth().user?.uid ?? null;
  const readOnly = !onEdit;
  const groups = useMemo(() => groupExpensesByDay(expenses, trip), [expenses, trip]);
  const groupRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [activeDay, setActiveDay] = useState<string>(groups[0]?.key ?? "");

  // Track which day is currently in view so the jump strip highlights
  // the right pill as the user scrolls — same approach as the
  // activities timeline.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const first = visible[0];
        if (first) {
          const key = (first.target as HTMLElement).dataset.dayKey;
          if (key) setActiveDay(key);
        }
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: [0, 0.25, 0.5] },
    );
    for (const el of groupRefs.current.values()) io.observe(el);
    return () => io.disconnect();
  }, [groups.length]);

  const jumpTo = (key: string) => {
    const el = groupRefs.current.get(key);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mt-5 flex flex-col gap-1">
      {groups.length > 1 ? (
        <div
          className="sticky top-[max(2.75rem,var(--safe-top))] z-[5] -mx-5 border-b border-line bg-[color-mix(in_oklab,var(--bg)_88%,transparent)] backdrop-blur-md px-5"
          aria-label="Jump to day"
        >
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto py-2">
            {groups.map((g) => {
              const on = g.key === activeDay;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => jumpTo(g.key)}
                  className={cn(
                    "shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors",
                    on ? "bg-primary text-white" : "bg-bg-sunken text-ink-muted hover:text-ink",
                  )}
                  aria-current={on ? "true" : undefined}
                >
                  {g.dayNumberLabel}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {groups.map((group) => (
        <section
          key={group.key}
          data-day-key={group.key}
          ref={(el) => {
            if (el) groupRefs.current.set(group.key, el);
            else groupRefs.current.delete(group.key);
          }}
          className="pt-4"
        >
          <header className="mb-2 px-1">
            <p className="text-[13px] font-semibold text-ink m-0">{group.dayNumberLabel}</p>
            <p className="text-[11px] font-bold uppercase tracking-wider text-ink-subtle m-0">
              {group.subtitle} · {group.dateLabel}
            </p>
          </header>
          <ul className="flex flex-col gap-2">
            {group.items.map((e) => {
              const isPayment = e.type === "payment";
              const Icon = iconForCategory(e.type);
              const color = colorForCategory(e.type);
              const splitInfo = isPayment
                ? `${e.payerName ?? e.creatorName} → ${e.splits[0]?.userName ?? "?"}`
                : e.splits.length > 1
                  ? `Split ${e.splits.length} ways`
                  : currentUid === (e.payerUid ?? e.creatorId)
                    ? "Paid by you"
                    : `Paid by ${e.payerName ?? e.creatorName}`;
              const rowInner = (
                <>
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ background: color + "22", color }}
                    aria-hidden
                  >
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-semibold text-ink m-0 truncate">
                      {isPayment ? "Payment" : e.name}
                    </p>
                    <p className="text-[12px] text-ink-subtle m-0 truncate">{splitInfo}</p>
                  </div>
                  <MoneyText
                    amount={e.amount}
                    currency={currency}
                    className="text-[15px] font-semibold text-ink"
                  />
                </>
              );
              return (
                <li key={e.id} className="rounded-lg border border-line bg-bg-elevated shadow-warm-1">
                  {readOnly ? (
                    <div className="flex w-full items-center gap-3 p-3">{rowInner}</div>
                  ) : (
                    // Tap opens the edit screen; delete lives there behind a
                    // confirmation. No row swipe — horizontal gestures collide
                    // with iOS edge-swipe navigation and caused accidental edits/deletes.
                    <button
                      type="button"
                      onClick={() => onEdit!(e)}
                      aria-label={`Edit ${isPayment ? "payment" : e.name}, ${formatMoney(e.amount, currency)}`}
                      className="flex w-full items-center gap-3 rounded-lg p-3 text-left"
                    >
                      {rowInner}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Bucket expenses by the LOCAL yyyy-MM-dd of expenseDate. Only days
 * with at least one expense get a group (no pre-registered empty
 * days — see the component doc above). "Day N" is anchored to the
 * trip's own start date so it always means the same thing as the
 * activities timeline's "Day N", even when earlier days had no
 * expenses; a payment logged outside the trip window (allowed —
 * record-payment doesn't clamp dates) falls back to a plain date
 * label instead of a nonsensical day number.
 */
function groupExpensesByDay(expenses: ExpenseEntry[], trip: Trip) {
  const map = new Map<string, ExpenseEntry[]>();
  const dayLabels = new Map<string, { label: string; subtitle: string }>();

  for (const e of expenses) {
    const d = e.expenseDate.toDate();
    const key = localYmd(d);
    if (!dayLabels.has(key)) {
      dayLabels.set(key, {
        label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        subtitle: d.toLocaleDateString("en-US", { weekday: "long" }),
      });
    }
    const arr = map.get(key) ?? [];
    arr.push(e);
    map.set(key, arr);
  }

  // Ascending within a day: expenseDate first, createdAt as a
  // tiebreaker — expenseDate has no real time-of-day (always local
  // noon, see parseYmdLocalNoon), so createdAt is what actually
  // reflects the order entries were made.
  for (const items of map.values()) {
    items.sort((a, b) => {
      const dt = a.expenseDate.toMillis() - b.expenseDate.toMillis();
      return dt !== 0 ? dt : a.createdAt.toMillis() - b.createdAt.toMillis();
    });
  }

  const start = trip.startDate.toDate();
  const startOfTrip = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const duration = trip.durationDays ?? Infinity;

  const entries = [...map.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return entries.map(([key, items]) => {
    const meta = dayLabels.get(key)!;
    const [y, m, d] = key.split("-").map(Number);
    const dayDate = new Date(y!, (m ?? 1) - 1, d);
    const dayNumber = Math.round((dayDate.getTime() - startOfTrip.getTime()) / 86_400_000) + 1;
    const inRange = dayNumber >= 1 && dayNumber <= duration;
    return {
      key,
      dayNumberLabel: inRange ? `Day ${dayNumber}` : meta.label,
      dateLabel: meta.label,
      subtitle: meta.subtitle,
      items,
    };
  });
}

function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── Who owes who ────────────────────────────────────────────
function FriendList({
  trip,
  expenses,
  currency,
  onEdit,
}: {
  trip: Trip;
  expenses: ExpenseEntry[];
  currency: string;
  /** Undefined means read-only — rows render as non-interactive divs. */
  onEdit?: (e: ExpenseEntry) => void;
}) {
  const currentUid = useAuth().user?.uid ?? null;
  const readOnly = !onEdit;
  // Direct debts: you owe whoever paid for your share. Never the
  // group-wide min-transfer plan, which can send you to someone you
  // shared nothing with.
  const debts = useMemo(() => computeDirectDebts(expenses), [expenses]);
  const payments = useMemo(() => expenses.filter((e) => e.type === "payment"), [expenses]);
  // Only the debts that involve you, from your side of each pair. There's
  // deliberately no whole-group view: balances between two other travelers
  // aren't actionable for you.
  const myDebts = useMemo(
    () => (currentUid ? debts.filter((d) => d.fromUserId === currentUid || d.toUserId === currentUid) : []),
    [debts, currentUid],
  );
  const groupSettled = debts.length === 0;

  // First time this trip lands on "all settled up" (per session), fire a
  // one-shot warm-palette confetti burst and a success haptic. The
  // sessionStorage flag inside celebrate() dedupes so the next tab-swap
  // doesn't re-trigger.
  useEffect(() => {
    if (groupSettled && expenses.length > 0) {
      celebrate(`settled:${trip.id}`, { pieces: 32, durationMs: 2200 });
    }
  }, [groupSettled, expenses.length, trip.id]);

  if (groupSettled) {
    return (
      <div className="mt-5 flex flex-col gap-4">
        <EmptyState
          icon={Wallet}
          title="All settled up"
          body="No outstanding balances between the group right now."
        />
        {!readOnly ? <RecordPaymentLink tripId={trip.id} /> : null}
        {payments.length > 0 ? (
          <PaymentHistoryList
            payments={payments}
            currency={currency}
            onEdit={onEdit}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-5 flex flex-col gap-4">
      {myDebts.length === 0 ? (
        <p className="text-[14px] text-ink-muted m-0">
          You're all square — the remaining balances are between other travelers.
        </p>
      ) : (
        <ul
          className="flex flex-col divide-y divide-line rounded-lg border border-line bg-bg-elevated shadow-warm-1"
          aria-label="Your balances"
        >
          {myDebts.map((d, i) => (
            <DebtRow
              key={`${d.fromUserId}-${d.toUserId}`}
              debt={d}
              colorSeed={i}
              currency={currency}
              currentUid={currentUid}
              tripId={trip.id}
              readOnly={readOnly}
            />
          ))}
        </ul>
      )}

      {!readOnly ? <RecordPaymentLink tripId={trip.id} /> : null}

      {payments.length > 0 ? (
        <PaymentHistoryList
          payments={payments}
          currency={currency}
          onEdit={onEdit}
        />
      ) : null}
    </div>
  );
}

/** Reachable even with no outstanding balances — logging a payment
 * doesn't require a live settle-up suggestion (e.g. paying someone
 * back for something unrelated to the computed minimum-transfer plan). */
function RecordPaymentLink({ tripId }: { tripId: string }) {
  return (
    <Link
      to={`/trip/${tripId}/record-payment`}
      className="inline-flex items-center gap-1.5 self-start px-1 text-[13px] font-semibold text-primary hover:underline"
    >
      <HandCoins className="h-3.5 w-3.5" />
      Record a payment
    </Link>
  );
}

/** Past settlement entries — editable/deletable so a mistaken or
 * partial payment can be corrected without recreating it. */
function PaymentHistoryList({
  payments,
  currency,
  onEdit,
}: {
  payments: ExpenseEntry[];
  currency: string;
  onEdit?: (e: ExpenseEntry) => void;
}) {
  const readOnly = !onEdit;
  const sorted = useMemo(
    () => [...payments].sort((a, b) => b.expenseDate.toMillis() - a.expenseDate.toMillis()),
    [payments],
  );

  return (
    <section aria-labelledby="payment-history-heading">
      <p
        id="payment-history-heading"
        className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle"
      >
        Payment history · {sorted.length}
      </p>
      <ul className="flex flex-col gap-2">
        {sorted.map((e) => {
          const rowInner = (
            <>
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                style={{ background: colorForCategory("payment") + "22", color: colorForCategory("payment") }}
                aria-hidden
              >
                <HandCoins className="h-4 w-4" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-ink m-0 truncate">
                  {e.payerName ?? e.creatorName} → {e.splits[0]?.userName ?? "?"}
                </p>
                <p className="text-[12px] text-ink-subtle m-0">
                  {e.expenseDate.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </p>
              </div>
              <MoneyText amount={e.amount} currency={currency} className="text-[14px] font-semibold text-ink" />
            </>
          );
          return (
            <li key={e.id} className="rounded-lg border border-line bg-bg-elevated shadow-warm-1">
              {readOnly ? (
                <div className="flex w-full items-center gap-3 p-3">{rowInner}</div>
              ) : (
                // Tap opens the edit screen; delete lives there behind a
                // confirmation. No row swipe — horizontal gestures collide
                // with iOS edge-swipe navigation and caused accidental edits/deletes.
                <button
                  type="button"
                  onClick={() => onEdit!(e)}
                  aria-label={`Edit payment, ${formatMoney(e.amount, currency)}`}
                  className="flex w-full items-center gap-3 rounded-lg p-3 text-left"
                >
                  {rowInner}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Utils ───────────────────────────────────────────────────
function LoadingBlock() {
  return (
    <div className="mx-auto max-w-2xl px-5 pt-4 flex flex-col gap-4" aria-busy>
      <Skeleton className="h-28 w-full rounded-xl" />
      <Skeleton className="h-10 w-full rounded-full" />
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-16 w-full rounded-lg" />
    </div>
  );
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function iconForCategory(t: ExpenseType) {
  const m: Record<ExpenseType, typeof Utensils> = {
    restaurants: Utensils,
    hotels: Hotel,
    flights: Plane,
    transportation: Bus,
    activities: Compass,
    shopping: ShoppingBag,
    tickets: Ticket,
    drinks: Coffee,
    groceries: ShoppingCart,
    insurance: Shield,
    payment: ArrowLeftRight,
    fees: Percent,
    misc: MoreHorizontal,
  };
  return m[t] ?? MoreHorizontal;
}

function colorForCategory(t: ExpenseType): string {
  const m: Record<ExpenseType, string> = {
    restaurants: "#E4572E",
    hotels: "#C97B3F",
    flights: "#4A7C9E",
    transportation: "#5E8A6E",
    activities: "#D08A3F",
    shopping: "#A16CB0",
    tickets: "#D9A85A",
    drinks: "#B87E28",
    groceries: "#5E8A6E",
    insurance: "#4A6FA5",
    payment: "#7C7369",
    fees: "#7C7369",
    misc: "#8B837C",
  };
  return m[t] ?? "#8B837C";
}

